import { Injectable } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { Location } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { ModalController } from "@ionic/angular";
import { LoadingService } from "@core/loading.service";
import { AuthService } from "@core/auth.service";
import { PaymentService } from "src/app/services/payment.service";
import { VoucherService } from "src/app/services/vouchers.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { StorePaymentComponent } from "src/app/pages/store/store-payment/store-payment.component";
import { RegProfileDialogComponentComponent } from "../reg-profile-dialog-component/reg-profile-dialog-component.component";
import { takeUntil, map } from "rxjs/operators";
import { Subject } from "rxjs";
import { Data } from "@angular/router";
import { StoreRepositoryService } from "src/app/pages/store/store-repository.service";

@Injectable()
export class StorePaymentFacade {
  public storeDiscountDetails: Data;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private modalController: ModalController,
    private loadingService: LoadingService,
    private authService: AuthService,
    private paymentService: PaymentService,
    private voucherService: VoucherService,
    private gtmService: GTMSegmentService,
    private matDialog: MatDialog,
    private storeService: StoreRepositoryService
  ) {
    this.storeService?.storeOffers$
      ?.pipe(takeUntil(this.destroy$))
      ?.subscribe({
        next: (value) => {
          this.storeDiscountDetails = value?.cash_offer_discount?.[0];
        },
        error: (error) => {
          this.authService.errorHandlerLogs(
            "[StorePaymentFacade] storeOffers$ subscription error:",
            error
          );
        },
      });
  }

  clickOfferBanner(): void {
    const offerId = "10783";

    this.authService
      ?.getOfferDetail(offerId)
      ?.pipe()
      ?.subscribe({
        next: (offer: Data) => {
          let data = Object.assign({
            line: offer?.lines - 1,
            real_name: offer?.lottery,
            prize: offer?.price,
            page: "store",
            productid: offer?.productid ? [...offer?.productid] : [],
            selectedDay: offer?.draw_day ? [...offer?.draw_day.map((m) => m?.trim())] : [],
            checkout: true,
            offers: offer,
            selectedDiscount: offer?.discount || this.storeDiscountDetails,
            selectedLine: offer?.lines - 1,
          });
          this.trackCTAClick(data);

          let queryParams = {
            TP1: "voucher_upsell",
          };

          data = { ...(data ?? {}), ...(queryParams ?? {}) };
          this.storePaymentPage(data);
        },
        error: (error) => {
          this.authService.errorHandlerLogs("Error occurred:", error);
          return error;
        },
      });
  }

  storePaymentPage(data: Data): void {
    const url = this.router
      .createUrlTree([], { relativeTo: this.activatedRoute, queryParams: { TP1: data?.TP1 } })
      .toString();
    this.location.go(url);
    this.modalController
      .create({
        component: StorePaymentComponent,
        cssClass: "modal-fullScreen",
        componentProps: {
          data: data,
        },
      })
      .then((paymentModal) => {
        this.loadingService.dismiss();
        paymentModal.present();
        paymentModal.onDidDismiss().then((res: Data) => {
          if (res?.data && res?.data?.p_type == "10") {
            const sqlAPICallResponse = res?.data?.syndicateId;
            if (!sqlAPICallResponse) {
              this.voucherService.setSyndicatePending();
              this.matDialog.open(RegProfileDialogComponentComponent, {
                panelClass: "ongoingWarning-size",
                backdropClass: "ongoingWarning-back-drop-color",
                disableClose: true,
              });
              return;
            }
          }
          if (res?.data && res?.data?.syndicateId) {
            this.paymentService
              .getSyndicateInfoWithProducts(res?.data?.syndicateId)
              .pipe(
                takeUntil(this.destroy$),
                map((detail: Data) => {
                  if (!detail?.[0]?.data()) return;
                  const syndicateInfo = detail?.[0].data();
                  syndicateInfo.product = [];
                  const productDocs = detail?.[1].docs;
                  for (let index = 0; index < productDocs?.length; index++) {
                    syndicateInfo.product.push(productDocs?.[index]?.data());
                  }
                  if (!syndicateInfo) return;
                  syndicateInfo["cardType"] = "Managed";
                  return syndicateInfo;
                })
              )
              ?.subscribe(
                (syndicateInfo) => {
                  this.showTicket(syndicateInfo, paymentModal);
                },
                (err) => {
                  return err;
                }
              );
          }
        });
      })
      .catch((err) => {
        return err;
      });
  }

  async showTicket(syndicateInfo: Data, paymentModal: HTMLIonModalElement) {
    const { SyndicateTicketsComponent } = await import("src/app/pages/syndicate/syndicate-tickets/syndicate-tickets.component");
    const syndicateTickets = await this.modalController.create({
      component: SyndicateTicketsComponent,
      componentProps: { syndicate: syndicateInfo },
      cssClass: "modal-fullScreen",
    });

    syndicateTickets
      .onDidDismiss()
      .then(() => {
        paymentModal.dismiss();
      })
      .catch((err) => {
        return err;
      });
    return await syndicateTickets.present();
  }

  trackCTAClick(body?: Data) {
    const message = "voucherupsell_clicked";
    this.gtmService?.GTMPopupInitialization(message, body);
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}